import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getCurrentUser, getUserRoles, getUsers } from '@/api/admin.api';
import { useAuth } from '@/context/AuthContext';

interface UserManagementContextType {
  isAdmin: boolean | null;
  isChecking: boolean;
}

const UserManagementContext = createContext<
  UserManagementContextType | undefined
>(undefined);

/** Attempts at reading the roles before a transient failure is accepted. */
const ROLE_CHECK_ATTEMPTS = 3;
const ROLE_CHECK_RETRY_MS = 1000;

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | null)?.response?.status;

/**
 * The account's backend id. A server without `/users/me` is asked by email,
 * and only an exact match counts: the search is a substring match.
 */
async function findBackendUserId(email: string | null): Promise<string | null> {
  try {
    return (await getCurrentUser()).id;
  } catch (error) {
    const status = statusOf(error);
    if ((status !== 404 && status !== 405) || !email) throw error;
    const users = await getUsers({ search: email, size: 50 });
    const match = users.items?.find((item) => item.email?.toLowerCase() === email.toLowerCase());
    return match?.id ?? null;
  }
}

/**
 * Whether the signed-in user is an administrator. The roles endpoint is
 * itself administrators-only, so a 403 there is the answer "no"; any other
 * failure is a failure to find out, retried, and never read as "no".
 */
export const UserManagementProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  // The account the current answer belongs to. A renewed token hands back a
  // new user object for the same account, which needs no second check.
  const checkedAccount = useRef<string | null>(null);

  const claims = user as { sub?: string; email?: string; preferred_username?: string } | null;
  const account = claims?.sub || claims?.email || claims?.preferred_username || null;
  const email = claims?.email || claims?.preferred_username || null;

  useEffect(() => {
    if (authLoading) {
      setIsChecking(true);
      return;
    }
    if (!isAuthenticated) {
      checkedAccount.current = null;
      setIsAdmin(false);
      setIsChecking(false);
      return;
    }
    if (!account) {
      setIsChecking(true);
      return;
    }
    // Already answered for this account: a loading phase that came and went
    // (a reloaded user record) ends the "checking" it started.
    if (checkedAccount.current === account) {
      setIsChecking(false);
      return;
    }

    let cancelled = false;
    const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    const check = async () => {
      // Another account's answer is not this one's.
      if (checkedAccount.current !== null) {
        checkedAccount.current = null;
        setIsAdmin(null);
      }
      setIsChecking(true);
      for (let attempt = 1; attempt <= ROLE_CHECK_ATTEMPTS; attempt += 1) {
        try {
          // The account's own record, not a search by email: a search is a
          // substring match and its first hit may be someone else.
          const userId = await findBackendUserId(email);
          if (cancelled) return;
          if (!userId) {
            checkedAccount.current = account;
            setIsAdmin(false);
            setIsChecking(false);
            return;
          }
          const roles = await getUserRoles(userId);
          if (cancelled) return;
          checkedAccount.current = account;
          setIsAdmin(
            roles.some((role) => role.id === 'admin' || role.name?.toLowerCase() === 'admin'),
          );
          setIsChecking(false);
          return;
        } catch (error) {
          if (cancelled) return;
          const status = statusOf(error);
          if (status === 401 || status === 403) {
            checkedAccount.current = account;
            setIsAdmin(false);
            setIsChecking(false);
            return;
          }
          if (attempt < ROLE_CHECK_ATTEMPTS) await wait(ROLE_CHECK_RETRY_MS * attempt);
          if (cancelled) return;
        }
      }
      // Still unknown after retrying: keep any earlier answer for this
      // session rather than hiding administration over a network failure.
      console.error('Could not check the administrator role');
      setIsAdmin((previous) => previous ?? false);
      setIsChecking(false);
    };
    void check();
    return () => {
      cancelled = true;
    };
  }, [account, email, authLoading, isAuthenticated]);

  return (
    <UserManagementContext.Provider
      value={{
        isAdmin,
        // Keep checking true if isAdmin is still null (not yet determined)
        isChecking: isChecking || authLoading || isAdmin === null,
      }}
    >
      {children}
    </UserManagementContext.Provider>
  );
};

export const useUserManagement = () => {
  const context = useContext(UserManagementContext);
  if (context === undefined) {
    throw new Error(
      'useUserManagement must be used within a UserManagementProvider'
    );
  }
  return context;
};
